/**
 * FILE: ads-chatbot.model.ts
 * CHỨC NĂNG: Model, dữ liệu và helper chatbot dùng chung cho Ads và Vpage
 * TÁC DỤNG: Danh sách kênh tạo bot, kịch bản mẫu (cùng id clone với Vpage), link hướng dẫn
 */
import {ISelectOption} from '@core/models';
import {BotItem} from '@shared/models/chatbot/script/chatbot.model';
import {CHANNEL, getChannelLogo} from '@shared/models/conversation';
import {normalizeChannelValue} from '@shared/utils';

/** URL module chatbot theo dạng /{module}/{controller}/{action} của Ads */
export const ADS_CHATBOT_BASE_PATH = 'chatbot/script';

/** Kênh được phép tạo chatbot — giống bước chọn nền tảng của Vpage */
export const ADS_CHATBOT_CHANNELS: readonly number[] = [
  CHANNEL.FACEBOOK,
  CHANNEL.ZALO_PERSONAL,
  CHANNEL.ZALO,
  CHANNEL.INSTAGRAM,
  CHANNEL.SHOPEE,
];

/** Kịch bản mẫu: tạo bot với cloneBotId = id */
export interface IAdsBotTemplate {
  id: string;
  nameKey: string;
  icon: string;
  link: string;
  channel: number;
}

export const ADS_CHATBOT_TEMPLATES: readonly IAdsBotTemplate[] = [
  {id: '1lRyv4kBRyIZ8NrD6s1Q', nameKey: 'feature.chatbot.templateShopClothing', icon: 'ph ph-t-shirt', link: 'https://m.me/118175664589731?ref=Kịch-bản-chào', channel: CHANNEL.FACEBOOK},
  {id: 'EEKZsogBRyIZ8NrD-4Oo', nameKey: 'feature.chatbot.templatePharmacy', icon: 'ph ph-pill', link: 'https://m.me/120522507686255?ref=Kịch-bản-chào', channel: CHANNEL.FACEBOOK},
  {id: 'hyCdsogBeVw7qjVx_VQz', nameKey: 'feature.chatbot.templateRestaurant', icon: 'ph ph-fork-knife', link: 'https://m.me/110533702030259?ref=Kịch-bản-chào', channel: CHANNEL.FACEBOOK},
  {id: 'l0KfsogBRyIZ8NrDiYN2', nameKey: 'feature.chatbot.templateKidsClothing', icon: 'ph ph-baby', link: 'https://m.me/120639104340882?ref=Kịch-bản-chào', channel: CHANNEL.FACEBOOK},
  {id: 'uyCfsogBeVw7qjVx7VQX', nameKey: 'feature.chatbot.templateSnackShop', icon: 'ph ph-cookie', link: 'https://m.me/100636106361951?ref=Kịch-bản-chào', channel: CHANNEL.FACEBOOK},
  {id: 'f1SnuokBRyIZ8NrDfXuK', nameKey: 'feature.chatbot.templateHotelBooking', icon: 'ph ph-bed', link: 'https://m.me/102141682875978?ref=Kịch-bản-chào', channel: CHANNEL.FACEBOOK},
  {id: 'MTCsuokBkQ0Bvxrq1OJy', nameKey: 'feature.chatbot.templateSpaBeauty', icon: 'ph ph-flower-lotus', link: 'https://m.me/119498914456151?ref=Kịch-bản-chào', channel: CHANNEL.FACEBOOK},
  {id: '5yChsogBeVw7qjVxYVSi', nameKey: 'feature.chatbot.templateCosmeticsShop', icon: 'ph ph-sparkle', link: 'https://m.me/122531854149885?ref=Kịch-bản-chào', channel: CHANNEL.FACEBOOK},
  {id: 'ALShsogBNpLiHvDf1wG2', nameKey: 'feature.chatbot.templatePerfumeShop', icon: 'ph ph-spray-bottle', link: 'https://m.me/108607748891414?ref=Kịch-bản-chào', channel: CHANNEL.FACEBOOK},
  {id: 'gYGisogBDuCCkXHmV2ri', nameKey: 'feature.chatbot.templateClinicBooking', icon: 'ph ph-stethoscope', link: 'https://m.me/112078861872639?ref=Kịch-bản-chào', channel: CHANNEL.FACEBOOK},
  {id: 'jMW4tYkBNpLiHvDfXaCK', nameKey: 'feature.chatbot.templatePetShop', icon: 'ph ph-paw-print', link: 'https://m.me/102262526197784?ref=Kịch-bản-chào', channel: CHANNEL.FACEBOOK},
  {id: 'E0KjsogBRyIZ8NrDqoSL', nameKey: 'feature.chatbot.templateTravelTour', icon: 'ph ph-airplane-tilt', link: 'https://m.me/105776962509698?ref=Kịch-bản-chào', channel: CHANNEL.FACEBOOK},
  {id: 'JUKksogBRyIZ8NrDHIQP', nameKey: 'feature.chatbot.templateRealEstate', icon: 'ph ph-house-line', link: 'https://m.me/101141372976707?ref=Kịch-bản-chào', channel: CHANNEL.FACEBOOK},
  {id: 'KSCtsogBeVw7qjVxmFaC', nameKey: 'feature.chatbot.customerCare', icon: 'ph ph-headset', link: '', channel: CHANNEL.ZALO},
];

/** Link hướng dẫn sử dụng kịch bản */
export const ADS_CHATBOT_MANUAL_LINK = 'https://vpage.manual.nhanh.vn/tinh-nang/chatbot/khoi-tao/kich-ban';

/** Danh sách fanpageId của bot (API trả string hoặc mảng) */
export function getBotFanpageIds(bot: BotItem | null | undefined): string[] {
  const ids = bot?.fanpageId as string[] | string | undefined;
  return (Array.isArray(ids) ? ids : ids ? [ids] : []).filter(Boolean);
}

/** Icon kênh của bot cho option d-select; imgCdn: transform của ImgCdnPipe */
export function getBotChannelIcon(channel: number | string | null | undefined, imgCdn: (path: string) => string): ISelectOption['iconLeft'] {
  switch (normalizeChannelValue(channel)) {
    case CHANNEL.FACEBOOK:
      return {classFont: 'ph-fill ph-facebook-logo text-primary fs-6'};
    case CHANNEL.INSTAGRAM:
      return {classFont: '', imageSrc: imgCdn(getChannelLogo(CHANNEL.INSTAGRAM))};
    case CHANNEL.ZALO:
      return {classFont: '', imageSrc: imgCdn(getChannelLogo(CHANNEL.ZALO))};
    case CHANNEL.ZALO_PERSONAL:
      return {classFont: '', imageSrc: imgCdn('logo/zalo_logo.png')};
    case CHANNEL.SHOPEE:
      return {classFont: '', imageSrc: imgCdn('logo/shopee_logo.png')};
    case CHANNEL.TIKTOK:
      return {classFont: '', imageSrc: imgCdn('logo/tiktok_shop.svg')};
    case CHANNEL.TIKTOK_BUSINESS:
      return {classFont: '', imageSrc: imgCdn('logo/tiktok.svg')};
    default:
      return {classFont: 'ph ph-robot'};
  }
}

export const ADS_CHATBOT_ICON_TONES = [
  'bg-danger bg-opacity-10 text-danger',
  'bg-purple bg-opacity-10 text-purple',
  'bg-warning bg-opacity-10 text-warning',
  'bg-success bg-opacity-10 text-success',
  'bg-primary bg-opacity-10 text-primary',
  'bg-secondary bg-opacity-10 text-secondary',
] as const;

export interface AdsChatbotCardType {
  type: string;
  title: string;
  question: string;
  channel: readonly number[];
}

/** Message types supported by the existing card builder and API. */
export const ADS_CHATBOT_CARD_TYPES: readonly AdsChatbotCardType[] = [
  {type: 'text', title: 'feature.chatbot.textCardSubtitle', question: 'feature.chatbot.textCardQuestion', channel: [CHANNEL.FACEBOOK, CHANNEL.ZALO, CHANNEL.SHOPEE, CHANNEL.INSTAGRAM, CHANNEL.ZALO_PERSONAL]},
  {type: 'quick_reply', title: 'feature.chatbot.quickReplyCardSubtitle', question: 'feature.chatbot.quickReplyCardSubtitle', channel: [CHANNEL.FACEBOOK]},
  {type: 'image', title: 'feature.chatbot.imageCardSubtitle', question: 'feature.chatbot.imageCardQuestion', channel: [CHANNEL.FACEBOOK, CHANNEL.SHOPEE, CHANNEL.INSTAGRAM, CHANNEL.ZALO_PERSONAL]},
  // Tạm ẩn: Ads chưa có GenAI
  // {type: 'ai_chat', title: 'Chatbot AI (ChatGPT, Gemini, DeepSeek...)', question: 'feature.chatbot.aiAssistant', channel: [CHANNEL.FACEBOOK, CHANNEL.ZALO, CHANNEL.SHOPEE, CHANNEL.INSTAGRAM, CHANNEL.ZALO_PERSONAL]},
  {type: 'image_text', title: 'feature.chatbot.imageTextCardSubtitle', question: 'feature.chatbot.imageTextCardQuestion', channel: [CHANNEL.ZALO]},
  {type: 'gallery', title: 'feature.chatbot.galleryCardSubtitle', question: 'feature.chatbot.galleryCardQuestion', channel: [CHANNEL.FACEBOOK]},
  {type: 'form', title: 'conversation.chatInput.requireAddress', question: 'feature.chatbot.formCardQuestion', channel: [CHANNEL.FACEBOOK, CHANNEL.ZALO]},
  {type: 'file', title: 'feature.chatbot.fileCardSubtitle', question: 'feature.chatbot.fileCardQuestion', channel: [CHANNEL.FACEBOOK, CHANNEL.ZALO, CHANNEL.INSTAGRAM]},
  {type: 'video', title: 'feature.chatbot.videoCardSubtitle', question: 'feature.chatbot.videoCardQuestion', channel: [CHANNEL.FACEBOOK, CHANNEL.ZALO_PERSONAL, CHANNEL.INSTAGRAM]},
  {type: 'audio', title: 'feature.chatbot.audioCardSubtitle', question: 'feature.chatbot.audioCardQuestion', channel: [CHANNEL.FACEBOOK, CHANNEL.INSTAGRAM]},
  {type: 'request_feedback', title: 'feature.chatbot.feedbackCardSubtitle', question: 'feature.chatbot.feedbackCardQuestion', channel: [CHANNEL.FACEBOOK]},
  {type: 'marketing', title: 'feature.chatbot.marketingCardSubtitle', question: 'feature.chatbot.marketingCardQuestion', channel: [CHANNEL.FACEBOOK]},
  {type: 'product_catalog', title: 'feature.chatbot.productCatalogCardTitle', question: 'feature.chatbot.productCatalogCardQuestion', channel: [CHANNEL.FACEBOOK]},
  {type: 'api_nhanh', title: 'API Nhanh.vn', question: 'feature.chatbot.apiNhanhCardQuestion', channel: [CHANNEL.FACEBOOK, CHANNEL.ZALO, CHANNEL.SHOPEE, CHANNEL.INSTAGRAM, CHANNEL.ZALO_PERSONAL]},
  {type: 'product_ecom', title: 'feature.chatbot.sendProduct', question: 'feature.chatbot.sendProduct', channel: [CHANNEL.SHOPEE]},
  {type: 'voucher_ecom', title: 'feature.chatbot.sendDiscountCode', question: 'feature.chatbot.sendDiscountCode', channel: [CHANNEL.SHOPEE]},
  {type: 'api', title: 'External API', question: 'feature.chatbot.callExternalApi', channel: [CHANNEL.FACEBOOK, CHANNEL.ZALO, CHANNEL.SHOPEE, CHANNEL.INSTAGRAM, CHANNEL.ZALO_PERSONAL]},
  {type: 'send_bank_card', title: 'feature.chatbot.sendBankTransfer', question: 'feature.chatbot.sendBankCardQuestion', channel: [CHANNEL.FACEBOOK]},
];

/** Màu icon cố định theo loại card (vị trí trong ADS_CHATBOT_CARD_TYPES) — bước "Thêm bước" và node sơ đồ cùng màu */
export function getAdsCardTone(type: string): string {
  const index = Math.max(ADS_CHATBOT_CARD_TYPES.findIndex(item => item.type === type), 0);
  return ADS_CHATBOT_ICON_TONES[index % ADS_CHATBOT_ICON_TONES.length];
}

export interface AdsChatbotAddCardRequest {
  id: number;
  blockId: string;
  type: string;
}

/** Tài khoản ngân hàng của DN (/ads/chatbot/bankaccount) — cùng dữ liệu tài khoản ngân hàng của Vpage */
export interface IAdsBankAccount {
  id: string;
  bankCode: string;
  binCode: string;
  accountNumber: string;
  accountName: string;
}

export type IAdsBankAccountForm = Omit<IAdsBankAccount, 'id'>;
